import { computed, DestroyRef, Directive, effect, inject, input, output } from '@angular/core';
import { ExtractedRow } from '../../models/extracted-values';
import { TableFormArrayCache } from '../../service/table-form-array-cache.service';
import { FormBuilder, FormControlStatus } from '@angular/forms';
import { ExtractedRowForm } from '../../models/extraction-form-models';
import { TableFormService } from '../../service/table/table-form.service';
import { TableOverlayStoreService } from '../../service/overlay/table-overlay-store.service';
import { TableDataCapturingConfig } from '../../models/interfaces';
import { noop } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Directive is only required here for inject / input / output / ... to work
@Directive()
export abstract class TableView {
  private readonly tableFormService = inject(TableFormService);

  protected readonly formBuilder = inject(FormBuilder);
  protected readonly tableOverlayStoreService = inject(TableOverlayStoreService);

  protected abstract tableFormArrayCache: TableFormArrayCache;
  protected abstract destroyRef: DestroyRef;

  readonly statusChange = output<FormControlStatus>();

  readonly tableValueChange = output<ExtractedRow[]>();

  readonly capturingConfig = input.required<TableDataCapturingConfig>();
  readonly formArrayCacheKey = input.required<string>();

  protected readonly document = computed(() => this.capturingConfig().document);
  protected readonly documentId = computed(() => this.document()?.id);
  protected readonly currentTableModel = computed(() => this.capturingConfig().tableModel);

  // TODO: Refactor such that we dont have to load all initial rows before getting the form array,
  //  if there already is cached form array for the given cache key...
  readonly initialRows = input.required<ExtractedRow[]>();

  /**
   * If there is a cached form array for the current document id, it is the result
   * of the computation. Otherwise, a new form array is created and cached.
   */
  protected readonly tableRows = computed(() => {
    const cachedFormArray = this.tableFormArrayCache.getFormArray(this.formArrayCacheKey());

    if (cachedFormArray) return cachedFormArray;

    const rows = this.initialRows();
    const formArray = this.formBuilder.array(rows.map((row) => this.createRowForm(row)));
    this.tableFormArrayCache.setFormArray(this.formArrayCacheKey(), formArray);
    return formArray;
  });

  protected constructor() {
    effect(() => {
      const formArray = this.tableRows();

      formArray.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.onTableRowsUpdated());

      formArray.statusChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((status) => this.statusChange.emit(status));

      formArray.updateValueAndValidity();
    });
  }

  protected createRowForm(row: ExtractedRow): ExtractedRowForm {
    return this.tableFormService.createRowForm(
      this.documentId(),
      row,
      this.currentTableModel(),
      noop,
      this.destroyRef,
    );
  }

  protected onTableRowsUpdated() {
    this.tableValueChange.emit(this.tableRows().getRawValue());
  }
}
